import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, LEGAL_OPERATOR, POLICY_EFFECTIVE_DATE } from '../config/legal';
// Single source of truth with the landing page — a policy that states a different
// minimum age from the marketing page is worse than one that states none.
import { MINIMUM_AGE } from '../config/site';

/**
 * Public privacy policy — no auth, deliberately not wrapped in PublicRoute (that
 * redirects signed-in users to /feed, which would be wrong for a legal page that
 * must be reachable by anyone, including App Review).
 *
 * This is the URL given to App Store Connect (required for external TestFlight and
 * for App Store submission) and linked from the iOS app's Profile screen.
 */
export default function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8">
                <h1 className="text-3xl font-bold text-gray-900">🎁 Wishful — Privacy Policy</h1>
                <p className="text-sm text-gray-500 mt-2">Last updated: {POLICY_EFFECTIVE_DATE}</p>

                <Section title="The short version">
                    <p>
                        Wishful is a gift wish-list app. We collect what we need to run it — your
                        account details, your wish lists, and the groups you share them with —
                        and nothing else.
                    </p>
                    <ul className="list-disc pl-5 mt-3 space-y-1">
                        <li>We <strong>do not track you</strong> across other apps or websites.</li>
                        <li>We <strong>do not show ads</strong> and there are no advertising networks in the app.</li>
                        <li>We <strong>do not sell or rent your data</strong> to anyone.</li>
                        <li>There are <strong>no third-party analytics or tracking SDKs</strong> in the iOS app.</li>
                        <li>You can <strong>delete your account and all your data</strong> from inside the app at any time.</li>
                    </ul>
                </Section>

                <Section title="Who we are">
                    <p>
                        Wishful ("we", "us") is operated by {LEGAL_OPERATOR}. The app is available
                        on iOS and on the web at{' '}
                        <a className="text-blue-600 hover:underline" href="https://gifts.memula.com">gifts.memula.com</a>.
                        If you have any question about this policy or your data, contact us at{' '}
                        <a className="text-blue-600 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    </p>
                </Section>

                <Section title="What we collect">
                    <p>Everything below is information you choose to give us by using the app.</p>
                    <Table
                        rows={[
                            ['Account', 'Your name, email address, and password. Your password is stored only as a salted hash — we never store or see the password itself.'],
                            ['Optional profile details', 'Birthday, phone number, profile photo, and a badge colour. All optional; the app works without them.'],
                            ['Your wish lists', 'The wishes you add: title, description, product links, prices, images, priority, quantity, and any notes.'],
                            ['Groups and events', 'Groups you create or join, who is in them, and events you add (such as birthdays).'],
                            ['Pets', 'If you add a pet you look after: its name, species, photo, birthday and "gotcha day", and its wish list.'],
                            ['Gift activity', 'Which gifts you have claimed or bought for other people, and any private note you attach.'],
                            ['Invitations', 'The email address you enter when you invite someone to a group.'],
                        ]}
                    />
                </Section>

                <Section title="What we do not collect">
                    <ul className="list-disc pl-5 space-y-2">
                        <li>
                            <strong>Your contacts are never uploaded.</strong> If you allow contacts
                            access, the app searches your contacts <em>on your device only</em>, to
                            suggest an email address as you type an invitation. The contact list is
                            never sent to our servers and never stored by us. You can decline access
                            and still invite people by typing an address.
                        </li>
                        <li>
                            <strong>No location data</strong>, no advertising identifiers, and no
                            browsing or usage tracking.
                        </li>
                        <li>
                            <strong>No payment details.</strong> Wishful never asks for card or bank
                            information. When you buy a gift you do that on the retailer's own site;
                            we only record that you marked it as bought.
                        </li>
                    </ul>
                </Section>

                <Section title="Keeping accounts safe">
                    <p>
                        To stop people guessing passwords or using the "forgot password" form to
                        flood someone's inbox, our server keeps short-lived counters of how many
                        sign-in and reset attempts have come from a given internet address. Email
                        addresses used in those counters are stored only as a one-way scrambled
                        value, never in readable form, and the counters expire on their own within
                        the hour.
                    </p>
                    <p className="mt-3">
                        Our server also keeps ordinary error logs so we can fix things that break.
                        They can incidentally contain an email address if it was part of whatever
                        went wrong. They are kept on our server, are read only by us, and are not
                        sent to any analytics or error-reporting company.
                    </p>
                </Section>

                <Section title="How we use it">
                    <p>We use your information only to run the app:</p>
                    <ul className="list-disc pl-5 mt-3 space-y-1">
                        <li>To sign you in and keep your account secure.</li>
                        <li>To show your wishes to the groups you have shared them with.</li>
                        <li>To let other people claim and buy gifts without spoiling the surprise.</li>
                        <li>To send you the emails described below.</li>
                    </ul>
                    <p className="mt-3">
                        We do not use your information for advertising, profiling, or automated
                        decision-making.
                    </p>
                </Section>

                <Section title="Who can see your information">
                    <ul className="list-disc pl-5 space-y-2">
                        <li>
                            <strong>People in your groups</strong> can see your name, profile photo,
                            badge colour, and any wish you have shared into that group. If you add a
                            birthday, group members can see it (that is the point — so they know when
                            to celebrate).
                        </li>
                        <li>
                            <strong>Keeping the surprise:</strong> when someone claims or buys a gift
                            from your list, the app deliberately hides that from <em>you</em>. You
                            cannot see who claimed or bought your gift, or their private note, until
                            it is revealed. This is enforced on our server, not just in the app.
                        </li>
                        <li>
                            <strong>Nobody outside your groups</strong> can see your wishes. Wishful
                            has no public profiles and no public wish lists.
                        </li>
                    </ul>
                </Section>

                <Section title="Emails we send">
                    <p>
                        We send email only for things you or someone you know asked for: group
                        invitations, and password-reset links. We do not send marketing email.
                        Emails are delivered through Fastmail, which processes the recipient's email
                        address in order to deliver the message.
                    </p>
                </Section>

                <Section title="Product links you paste">
                    <p>
                        When you paste a product link into a wish (or share one into Wishful from
                        another app), our server fetches that page to pick up the product's title and
                        image. The retailer's site may see that request, in the same way it would see
                        any visit. We do not send them anything about you.
                    </p>
                </Section>

                <Section title="Where your data is stored, and for how long">
                    <p>
                        Your data is stored on our servers in the United States and is sent between
                        your device and us over an encrypted HTTPS connection. On iOS, your sign-in
                        token is stored in the device Keychain.
                    </p>
                    <p className="mt-3">
                        We keep your information for as long as your account exists. If you stop
                        using Wishful, you can delete your account at any time — see below.
                    </p>
                </Section>

                <Section title="Deleting your account">
                    <p>
                        You can permanently delete your account from inside the app:{' '}
                        <strong>Profile → Delete account</strong> (or on the web, from your Profile
                        page). We ask for your password to confirm, and then the deletion is
                        immediate and permanent. It is not a deactivation and there is no recovery.
                    </p>
                    <p className="mt-3">When you delete your account:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Your account, wish lists, pets and their wish lists are deleted.</li>
                        <li>Your profile photo is deleted.</li>
                        <li>You are signed out on every device.</li>
                        <li>
                            Gifts you had claimed or bought for other people are released back onto
                            their lists so someone else can pick them up, and your private notes on
                            them are deleted.
                        </li>
                        <li>
                            Groups you created are handed to another admin so the group survives for
                            its members. A group is deleted only if you were its only member.
                        </li>
                    </ul>
                </Section>

                <Section title="Your rights">
                    <p>
                        You can view and change your name, birthday, phone number and photo at any
                        time from your Profile, and you can edit or delete individual wishes and
                        groups. You can delete everything by deleting your account. Depending on
                        where you live, you may have additional rights over your personal data —
                        including the right to request a copy of it. Contact us at{' '}
                        <a className="text-blue-600 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
                        and we will help.
                    </p>
                </Section>

                <Section title="Children">
                    <p>
                        <strong>Wishful accounts are for people aged {MINIMUM_AGE} and over.</strong>{' '}
                        We do not knowingly collect personal information from children under{' '}
                        {MINIMUM_AGE}, and the app is not directed at them.
                    </p>
                    <p className="mt-3">
                        Families do use Wishful together, and there is a way to include a younger
                        child without giving them an account. An adult can add a child to a group
                        and keep a wish list on their behalf. The child never signs in, never gives
                        us an email address, and has no account of their own.
                    </p>
                    <p className="mt-3">
                        For such a list we store only the child&rsquo;s <strong>first name</strong>{' '}
                        and, if the adult chooses to add it, a <strong>birthday</strong> — the least
                        that makes a wish list usable. Both are visible to the members of the group
                        the child was added to, which is the point: those are the people buying the
                        gifts. Nothing else about the child is collected.
                    </p>
                    <p className="mt-3">
                        More than one adult can be a <strong>carer</strong> for a child&rsquo;s list,
                        so both parents (or a grandparent) can add to it. Unlike the rest of Wishful,
                        carers <em>can</em> see what has already been bought from a child&rsquo;s
                        list — there is no surprise to protect, since the child never sees it, and it
                        stops two adults buying the same present. Deleting the child, or the account
                        of the last carer, removes the list and everything on it.
                    </p>
                    <p className="mt-3">
                        If you believe a child under {MINIMUM_AGE} has created their own account,
                        contact us at{' '}
                        <a className="text-blue-600 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
                        and we will delete it and its data.
                    </p>
                </Section>

                <Section title="Changes to this policy">
                    <p>
                        If we change this policy we will update the date at the top of this page. If
                        the change is significant, we will tell you in the app.
                    </p>
                </Section>

                <div className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-500 flex gap-4">
                    <Link to="/support" className="text-blue-600 hover:underline">Support</Link>
                    <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
            <div className="text-gray-700 leading-relaxed">{children}</div>
        </section>
    );
}

function Table({ rows }) {
    return (
        <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
                <tbody>
                    {rows.map(([label, detail]) => (
                        <tr key={label} className="border-b border-gray-100 align-top">
                            <th scope="row" className="py-2 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                                {label}
                            </th>
                            <td className="py-2 text-gray-700">{detail}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
